import React, { Fragment, useEffect, useState, useMemo } from 'react';
import { connect } from 'react-redux';
import { get, find, isEmpty } from 'lodash';
import MaterialButton from '@material-ui/core/Button';
import Grid from '@material-ui/core/Grid';
import Popover from '@material-ui/core/Popover';
import ArrowBackIcon from '@material-ui/icons/ArrowBack';
import DateRangeIcon from '@material-ui/icons/DateRange';
import DeleteIcon from '@material-ui/icons/Delete';
import ExpandMoreIcon from '@material-ui/icons/ExpandMore';
import classnames from 'classnames';

import DefaultMaterialTable from 'common/util/materialTable';
import { optionsBuilder } from 'common/util/materialTable/options';
import { LoadingStatus } from 'components/common/LoadingStatus/LoadingStatus';
import { customTheme } from 'common/util/materialTable/theme';
import { Button } from 'components/common/Button/Button';
import DialogRemoveUsers from '../components/DialogRemoveUsers';
import AddEmailDrawler from './AddEmailDrawer';
import { fetchEmailConfigs, resetEmailConfigs, removeUsersFromEmailList } from '../actions/reportActions';
import { reportLinks } from '../utils/constants';
import styles from './EmailConfig.scss';
import reportStyles from './ReportContent.scss';

const tableTheme = customTheme({
    MuiPaper: {
        root: {
            width: '99%',
            margin: '0 auto',
            boxShadow: 'none !important',
        },
    },
    MuiTable: {
        root: {
            width: '100%',
            margin: '0 auto',
            borderBottom: 'none !important',
        },
    },

    MuiSvgIcon: {
        fontSizeSmall: {
            fontSize: '1.5rem',
        },
    },
});

const tableOptions = optionsBuilder({
    search: false,
    filtering: false,
    toolbar: true,
    selection: true,
    emptyRowsWhenPaging: false,
});

export const localization = {
    header: {
        actions: 'Actions',
    },
};

const groupCountText = (groupCount) => {
    return groupCount > 1 ? `${groupCount} Groups` : `${groupCount} Group`;
};

const renderGroup = props => {
    const [anchorEl, setAnchorEl] = useState(null);

    const handleClick = event => {
        setAnchorEl(event.currentTarget);
    };

    const handleClose = () => {
        setAnchorEl(null);
    };

    const open = Boolean(anchorEl);
    const id = open ? 'simple-popover' : undefined;

    return (
        <div data-cy="EmailConfig">
            <MaterialButton
                className={classnames(styles.iconAndText, styles.buttonGroups)}
                endIcon={<ExpandMoreIcon />}
                onClick={handleClick}
            >
                {groupCountText(props.groups.length)}
            </MaterialButton>
            <Popover
                className={styles.groupsContainer}
                id={id}
                open={open}
                anchorEl={anchorEl}
                onClose={handleClose}
                anchorOrigin={{
                    vertical: 'bottom',
                    horizontal: 'right',
                }}
                transformOrigin={{
                    vertical: 'top',
                    horizontal: 'right',
                }}
            >
                <div className={styles.groupsContent} data-cy="EmailConfig-div">
                    <ul className={styles.groupsList} data-cy="EmailConfig-ul">
                        <li className={styles.groupsTitle} data-cy="EmailConfig-li">{groupCountText(props.groups.length)}</li>
                        {props.groups.map((group, index) => (
                            <li key={index} data-cy={'EmailConfig-li' + index}>{group}</li>
                        ))}
                    </ul>
                </div>
            </Popover>
        </div>
    );
};

const getColumns = onUpdateAction => {
    return [
        {
            title: 'Name',
            field: 'user.displayName',
            hidden: false,
            headerStyle: {
                minWidth: '180px',
                maxWidth: '220px',
            },
            cellStyle: {
                minWidth: '180px',
                maxWidth: '220px',
                fontSize: '16px',
                wordWrap: 'break-word',
            },
        },
        {
            title: 'Email',
            field: 'user.emailAddress',
            hidden: false,
            headerStyle: {
                minWidth: '160px',
                maxWidth: '160px',
            },
            cellStyle: {
                minWidth: '160px',
                maxWidth: '160px',
                fontSize: '16px',
                wordWrap: 'break-word',
            },
        },
        {
            title: 'Groups',
            field: 'groups',
            hidden: false,
            emptyValue: <p className={styles.emptyLabel}>No Groups Provided</p>,
            headerStyle: {
                minWidth: '100px',
                maxWidth: '100px',
            },
            cellStyle: {
                minWidth: '100px',
                maxWidth: '100px',
                fontSize: '14px',
                wordWrap: 'break-word',
            },
            customSort: (a, b) => a.groups.length - b.groups.length,
            render: renderGroup,
        },
        {
            title: 'Actions',
            field: '',
            hidden: false,
            headerStyle: {
                minWidth: '200px',
                maxWidth: '200px',
            },
            cellStyle: {
                minWidth: '200px',
                maxWidth: '200px',
                fontSize: '14px',
                whiteSpace: 'nowrap',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
            },
            sorting: false,
            render: rowData => {
                const clickHandler = event => {
                    onUpdateAction && onUpdateAction(event, rowData);
                };
                return (
                    <div className={styles.schedulerAction} onClick={clickHandler} data-cy="EmailConfig-getColumns">
                        <DateRangeIcon /> <span>Scheduler Reminder</span>
                    </div>
                );
            },
        },
    ];
};

const components = {
    Toolbar: props => {
        const { actions, selectedRows } = props;
        const [dialogRemoveUserOpen, setDialogRemoveUserOpen] = useState(false);

        const toggleDialogRemoveUser = () => {
            setDialogRemoveUserOpen(!dialogRemoveUserOpen);
        };

        const removeUsers = () => {
            const findAction = actions.find(a => a.icon === 'remove');
            if (findAction) {
                findAction.onClick(selectedRows);
            }
        };
        return (
            <Fragment>
                {!isEmpty(selectedRows) && (
                    <div className={styles.deleteButtonWrapper}>
                        <MaterialButton
                            className={styles.iconAndText}
                            classes={{ iconSizeMedium: styles.mediumSize }}
                            startIcon={<DeleteIcon />}
                            onClick={toggleDialogRemoveUser}
                        >
                            Remove
                        </MaterialButton>
                    </div>
                )}
                <DialogRemoveUsers
                    dialogOpen={dialogRemoveUserOpen}
                    toggleDialog={toggleDialogRemoveUser}
                    onConfirm={removeUsers}
                />
            </Fragment>
        );
    },
};

export const EmailConfig = ({
    goBackReport,
    reportType,
    isLoading,
    emailConfigData,
    fetchEmailConfigs,
    resetEmailConfigs,
    removeUsersFromEmailList,
    institutionId,
}) => {
    const [open, setOpen] = useState(false);
    const [selectedEmailConfig, setSelectedEmailConfig] = useState();

    useEffect(() => {
        fetchEmailConfigs(reportType, institutionId);

        return () => {
            resetEmailConfigs(reportType);
        };
    }, []);

    const reportLink = find(reportLinks, { type: reportType });
    const goBackClickHandler = () => {
        goBackReport();
    };

    const addEmailClickHandler = () => {
        setOpen(true);
    };

    const updateEmailClickHandler = (event, data) => {
        setOpen(true);
        setSelectedEmailConfig(data);
    };

    const onCloseHandler = openState => {
        setOpen(openState);
        setSelectedEmailConfig(undefined);
    };

    const onRemoveHandler = async data => {
        const ids = data.map(item => item.id);
        await removeUsersFromEmailList(ids, institutionId);
        fetchEmailConfigs(reportType, institutionId);
    };

    const columns = useMemo(() => {
        return getColumns(updateEmailClickHandler);
    }, [updateEmailClickHandler]);

    if (isLoading) {
        return <LoadingStatus actionLabel={'loading'} itemName={''} loading={true} filterQuery={false} />;
    }

    return (
        <Grid className={reportType} container data-cy="EmailConfig-Grid">
            <Grid
                className={reportStyles.buttonGroups}
                container
                direction="row"
                alignItems="center"
                justifyContent="space-between"
            >
                <MaterialButton
                    className={styles.iconAndText}
                    classes={{ iconSizeMedium: styles.mediumSize }}
                    startIcon={<ArrowBackIcon />}
                    onClick={goBackClickHandler}
                    data-cy="EmailConfig-onClick"
                >
                    Back to {reportLink.title} Reports
                </MaterialButton>

                <div className={reportStyles.rightButtons} data-cy="EmailConfig-divs">
                    <Button className={styles.addEmail} onClick={addEmailClickHandler} data-cy="EmailConfig-Add">
                        Add Email
                    </Button>
                    <AddEmailDrawler
                        key={open.toString()}
                        open={open}
                        onClose={onCloseHandler}
                        reportType={reportType}
                        selectedEmailConfig={selectedEmailConfig}
                    />
                </div>
            </Grid>
            <Grid container>
                <DefaultMaterialTable
                    key={reportType}
                    theme={tableTheme}
                    localization={localization}
                    options={tableOptions}
                    columns={columns}
                    data={emailConfigData}
                    components={components}
                    actions={[
                        {
                            icon: 'remove',
                            tooltip: '',
                            isFreeAction: true,
                            onClick: onRemoveHandler,
                        },
                    ]}
                />
            </Grid>
        </Grid>
    );
};

export default connect(
    (state, props) => {
        const goBackReport = props.goBackReport;
        const reportType = props.reportType;
        const { report = {} } = state;
        const isLoading = get(report, `${reportType}.isLoadingEmailConfig`, false);
        const emailConfigData = get(report, `${reportType}.emailConfigData`, []);
        const institutionId = get(state, 'me.institution.id');

        return {
            goBackReport,
            reportType,
            isLoading,
            emailConfigData,
            institutionId,
        };
    },
    {
        fetchEmailConfigs,
        resetEmailConfigs,
        removeUsersFromEmailList,
    },
)(EmailConfig);
